// src/components/shared/AgeGate.js
import React, { useState } from 'react';
import { useApp } from '../../lib/AppContext';
import { COUNTRY_CONFIG } from '../../lib/currency';
import { logAgeGateEvent } from '../../lib/compliance';


const LANG_OPTIONS = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰' },
  { code: 'hu', label: 'Magyar',     flag: '🇭🇺' },
  { code: 'ro', label: 'Română',     flag: '🇷🇴' },
  { code: 'pl', label: 'Polski',     flag: '🇵🇱' },
  { code: 'hr', label: 'Hrvatski',   flag: '🇭🇷' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'at', label: 'Österreich', flag: '🇦🇹' },
];

// Simple translations for the age gate itself
const AGE_STRINGS = {
  en: { title: 'Age Verification',    body: 'You must be 18 or older to enter CosmiqHerbz.',     enter: 'I am 18+ — Enter', exit: 'Under 18 — Exit', note: 'All products are sold as collector\'s items only — not for consumption.', lang: 'Choose your language', country: 'Shipping to' },
  de: { title: 'Altersverifizierung', body: 'Sie müssen 18 Jahre oder älter sein, um einzutreten.', enter: 'Ich bin 18+ — Eintreten', exit: 'Unter 18 — Verlassen', note: 'Alle Produkte werden nur als Sammlerstücke verkauft — nicht zum Verzehr.', lang: 'Sprache wählen', country: 'Versand nach' },
  sk: { title: 'Overenie veku',       body: 'Musíte mať 18 rokov alebo viac, aby ste vstúpili.',  enter: 'Mám 18+ — Vstúpiť', exit: 'Pod 18 — Odísť', note: 'Všetky produkty sa predávajú iba ako zberateľské predmety — nie na konzumáciu.', lang: 'Vyberte jazyk', country: 'Doručenie do' },
  hu: { title: 'Korhatár-ellenőrzés', body: 'Legalább 18 évesnek kell lennie a belépéshez.',     enter: '18+ vagyok — Belépés', exit: '18 alatt — Kilépés', note: 'Minden terméket kizárólag gyűjtői célokra értékesítünk — nem fogyasztásra.', lang: 'Válasszon nyelvet', country: 'Szállítás ide' },
  ro: { title: 'Verificare vârstă',   body: 'Trebuie să aveți cel puțin 18 ani pentru a intra.',  enter: 'Am 18+ — Intră', exit: 'Sub 18 — Ieși', note: 'Toate produsele sunt vândute exclusiv ca articole de colecție — nu pentru consum.', lang: 'Alegeți limba', country: 'Livrare în' },
  pl: { title: 'Weryfikacja wieku',   body: 'Musisz mieć co najmniej 18 lat, aby wejść.',        enter: 'Mam 18+ — Wejdź', exit: 'Poniżej 18 — Wyjdź', note: 'Wszystkie produkty sprzedawane są wyłącznie jako przedmioty kolekcjonerskie — nie do spożycia.', lang: 'Wybierz język', country: 'Wysyłka do' },
  hr: { title: 'Provjera dobi',       body: 'Morate imati najmanje 18 godina za ulaz.',           enter: 'Imam 18+ — Ulaz', exit: 'Ispod 18 — Izlaz', note: 'Svi proizvodi se prodaju isključivo kao kolekcionarski predmeti — nisu namijenjeni konzumaciji.', lang: 'Odaberite jezik', country: 'Dostava u' },
  nl: { title: 'Leeftijdsverificatie', body: 'U moet 18 jaar of ouder zijn om binnen te komen.',  enter: 'Ik ben 18+ — Ga naar binnen', exit: 'Onder 18 — Vertrek', note: 'Alle producten worden uitsluitend verkocht als verzamelobjecten — niet voor consumptie.', lang: 'Kies taal', country: 'Verzending naar' },
  at: { title: 'Altersverifizierung', body: 'Sie müssen 18 Jahre oder älter sein, um einzutreten.', enter: 'Ich bin 18+ — Eintreten', exit: 'Unter 18 — Verlassen', note: 'Alle Produkte werden nur als Sammlerstücke verkauft — nicht zum Verzehr.', lang: 'Sprache wählen', country: 'Versand nach' },
};

export default function AgeGate() {
  const { verifyAge } = useApp();
  const [selectedLang, setSelectedLang] = useState('en');
  const [selectedCountry, setSelectedCountry] = useState('DE');

  const s = AGE_STRINGS[selectedLang] || AGE_STRINGS.en;

  const handleEnter = () => {
    logAgeGateEvent('passed', selectedCountry, selectedLang, null);
    verifyAge(selectedLang, selectedCountry);
  };
  const handleExit = () => {
    logAgeGateEvent('exited', selectedCountry, selectedLang, null);
    window.location.href = 'https://www.google.com';
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(26,122,62,.15) 0%, transparent 70%), #000',
      zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        width: '100%', maxWidth: 480,
        background: '#0e0e0e',
        border: '1px solid rgba(76,202,116,.2)',
        borderRadius: 24,
        padding: '40px 36px',
        textAlign: 'center',
      }}>
        {/* Logo */}
        <img src="/logo.png" alt="CosmiqHerbz" className="agegate-logo" />

        {/* Language picker */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '.7rem', fontWeight: 700,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: '#4cca74', marginBottom: '10px',
          }}>
            {s.lang}
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap',
            gap: '6px', justifyContent: 'center',
          }}>
            {LANG_OPTIONS.map(l => (
              <button
                key={l.code}
                onClick={() => setSelectedLang(l.code)}
                style={{
                  background: selectedLang === l.code ? 'rgba(76,202,116,.15)' : 'rgba(255,255,255,.04)',
                  border: `1px solid ${selectedLang === l.code ? 'rgba(76,202,116,.4)' : 'rgba(255,255,255,.08)'}`,
                  borderRadius: '20px',
                  padding: '5px 12px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '5px',
                  transition: 'all .2s',
                }}
              >
                <span style={{ fontSize: '.9rem' }}>{l.flag}</span>
                <span style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: '.72rem', fontWeight: 700,
                  letterSpacing: '.05em',
                  color: selectedLang === l.code ? '#4cca74' : '#666',
                }}>
                  {l.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Country picker */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '.7rem', fontWeight: 700,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: '#4cca74', marginBottom: '10px',
          }}>
            {s.country}
          </div>
          <div style={{
            display: 'flex', flexWrap: 'wrap',
            gap: '6px', justifyContent: 'center',
          }}>
            {Object.entries(COUNTRY_CONFIG).map(([code, cfg]) => (
              <button
                key={code}
                onClick={() => setSelectedCountry(code)}
                style={{
                  background: selectedCountry === code ? 'rgba(76,202,116,.15)' : 'rgba(255,255,255,.04)',
                  border: `1px solid ${selectedCountry === code ? 'rgba(76,202,116,.4)' : 'rgba(255,255,255,.08)'}`,
                  borderRadius: '20px',
                  padding: '5px 12px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '5px',
                  transition: 'all .2s',
                }}
              >
                <span style={{ fontSize: '.9rem' }}>{cfg.flag}</span>
                <span style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: '.72rem', fontWeight: 700,
                  color: selectedCountry === code ? '#4cca74' : '#666',
                }}>
                  {code}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Age gate title */}
        <h2 style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 'clamp(1.4rem,4vw,1.8rem)',
          letterSpacing: '.06em',
          color: '#f4f4f4', marginBottom: '10px',
        }}>
          {s.title}
        </h2>
        <p style={{
          color: '#777', fontSize: '.88rem',
          fontWeight: 300, lineHeight: 1.8,
          marginBottom: '28px', maxWidth: 340, margin: '0 auto 28px',
        }}>
          {s.body}
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: 320, margin: '0 auto' }}>
          <button
            onClick={handleEnter}
            style={{
              background: 'var(--green, #4cca74)', color: '#000',
              border: 'none', borderRadius: '25px',
              padding: '14px 32px',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '.95rem', fontWeight: 800,
              letterSpacing: '.12em', textTransform: 'uppercase',
              cursor: 'pointer', transition: 'all .25s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#fff'}
            onMouseOut={e  => e.currentTarget.style.background = '#4cca74'}
          >
            {s.enter}
          </button>
          <button
            onClick={handleExit}
            style={{
              background: 'transparent', color: '#555',
              border: '1px solid #333', borderRadius: '25px',
              padding: '14px 32px',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '.95rem', fontWeight: 700,
              letterSpacing: '.12em', textTransform: 'uppercase',
              cursor: 'pointer', transition: 'all .25s',
            }}
            onMouseOver={e => { e.currentTarget.style.color = '#888'; e.currentTarget.style.borderColor = '#555'; }}
            onMouseOut={e  => { e.currentTarget.style.color = '#555'; e.currentTarget.style.borderColor = '#333'; }}
          >
            {s.exit}
          </button>
        </div>

        {/* Disclaimer */}
        <p style={{
          marginTop: '24px', fontSize: '.72rem',
          color: '#333', lineHeight: 1.8, maxWidth: 340, margin: '24px auto 0',
        }}>
          {s.note}
        </p>
      </div>
    </div>
  );
}